import { useRef, useEffect, useCallback } from 'preact/hooks';
import {
  convertUseAnimationCssPropertiesToRegularCSS,
  convertUseAnimationCssPropertiesToWAAPIKeyframe,
  UseAnimationCssProperties,
  stopAnimation
} from './animationUtils';

export type UseAnimationConfig<V extends string, E extends HTMLElement> = {
  animationStates: Partial<
    Record<AnimationStateKey<V>, ((node: E) => AnimationConfig) | AnimationConfig>
  >;
  isAnimatedOnMount?: boolean;
  onAnimationEnd?: ({ animationState }: { animationState: V }) => void;
};

// Used to filter simple states and ones that include previous state
type AnimationStateKey<PA extends string> = ExtractAnimationStates<PA | `${PA} => ${PA}`>;

// Used to get previous and current states and do deeper typing
type ExtractAnimationStates<StateString extends string> =
  StateString extends `${infer From} => ${infer To}`
    ? KeyWithPreviousAndCurrentState<From, To>
    : StateString;

// Used to just allow keys with different previous and current states
type KeyWithPreviousAndCurrentState<PreviousState extends string, CurrentState extends string> = {
  b: PreviousState;
} extends { b: CurrentState }
  ? never
  : `${PreviousState} => ${CurrentState}`;

type AnimationConfig = {
  from?: UseAnimationCssProperties;
  to: UseAnimationCssProperties;
  end?: UseAnimationCssProperties;
  options?: AnimationOptions;
};

type AnimationOptions = {
  delay?: number;
  duration?: number;
  easing?:
    | 'linear'
    | 'ease'
    | 'ease-in'
    | 'ease-out'
    | 'ease-in-out'
    | [number, number, number, number];
};

// Used to exclude states that include => since this string is used to represent a transition from state to another.
type PermittedAnimationState<V extends string> = Exclude<V, `${string}=>${string}`>;

/**
 * Hook to animate single components.
 * It allows n number of animation states.
 * @param animationState
 * @param animationConfig
 * @returns
 */
export function useAnimation<V extends string, E extends HTMLElement = HTMLElement>(
  animationState: PermittedAnimationState<V>,
  {
    animationStates,
    isAnimatedOnMount = false,
    onAnimationEnd = () => {}
  }: UseAnimationConfig<PermittedAnimationState<V>, E>
) {
  const didMountRef = useRef(false);
  const nodeRef = useRef<E | null>(null);
  const currentAnimationState = useRef<PermittedAnimationState<V>>();
  const currentAnimation = useRef<Animation>();

  // We don't allow changing animationStates. Ref to keep "first run" animation states.
  const animationStatesRef =
    useRef<UseAnimationConfig<PermittedAnimationState<V>, E>['animationStates']>(animationStates);

  useEffect(() => {
    // UseEffect just have to be ran when there is a change on animationState
    if (currentAnimationState.current === animationState) return;

    // It makes sense to keep track of animation states even if there's no animated element to animate.
    const previousAnimationState = currentAnimationState.current;
    currentAnimationState.current = animationState;

    //Stop an animation here only makes sense if the previous one is running
    if (currentAnimation.current?.playState === 'running') {
      stopAnimation(currentAnimation.current, nodeRef.current);
    }

    const animatedElement = nodeRef.current;
    if (!animatedElement) {
      return;
    }

    if (!didMountRef.current) {
      didMountRef.current = true;
      if (!isAnimatedOnMount) {
        return;
      }
    }

    const animationConfig = getConfig(
      animationStatesRef.current,
      animatedElement,
      animationState,
      previousAnimationState
    );

    if (!animationConfig) {
      return;
    }

    currentAnimation.current = startAnimation(
      animationConfig,
      animatedElement,
      animationState,
      onAnimationEnd
    );
  }, [animationState, isAnimatedOnMount, onAnimationEnd]);

  /**
   * Used to cancel current animation. When animation is canceled node style goes back to beginning style of canceled animation.
   * @returns
   */
  const cancelCurrentAnimation = useCallback(() => {
    const animation = currentAnimation.current;
    if (animation?.playState === 'running') {
      animation.cancel();
    }
  }, []);

  /**
   * Callback ref use to set nodeRef.
   *
   * @param node Actual element user set.
   * @returns
   */
  const callbackRef = useCallback((node: E | null) => {
    if (node != nodeRef.current) {
      //If for any reason element is unmounted we have to cancel animation(if there is one) and
      //set currentAnimation as undefined so we don't keep running this animation
      if (nodeRef.current) {
        currentAnimation.current?.cancel();
        currentAnimation.current = undefined;
      }
      nodeRef.current = node;
    }
  }, []);

  return { nodeRef: callbackRef, controller: { cancel: cancelCurrentAnimation } };
}

/**
 * Gets animation configuration.
 *
 * @param animationStates Set of animation configurations provided by the user.
 * @param animatedElement Element that is going to be styled.
 * @param animationState Current animation state.
 * @param previousAnimationState Previous animation state.
 * @returns AnimationConfig.
 */
function getConfig<V extends string, E extends HTMLElement>(
  animationStates: UseAnimationConfig<PermittedAnimationState<V>, E>['animationStates'],
  animatedElement: E,
  animationState: PermittedAnimationState<V>,
  previousAnimationState?: PermittedAnimationState<V>
) {
  const currentAnimationStateConfig =
    animationStates[
      `${previousAnimationState} => ${animationState}` as keyof UseAnimationConfig<
        PermittedAnimationState<V>,
        E
      >['animationStates']
    ] ||
    animationStates[
      animationState as keyof UseAnimationConfig<PermittedAnimationState<V>, E>['animationStates']
    ];

  const animationConfig =
    typeof currentAnimationStateConfig === 'function'
      ? currentAnimationStateConfig(animatedElement)
      : (currentAnimationStateConfig as AnimationConfig);

  return animationConfig && Object.keys(animationConfig).length > 0 ? animationConfig : null;
}

/**
 * Starts an animation.
 *
 * @param animationConfig Animation configuration used by the animation.
 * @param animatedElement Element that is going to be styled.
 * @param animationState Current animation state.
 * @param onAnimationEnd Callback called once the animation is completed.
 * @returns Animation.
 */
function startAnimation<V extends string, E extends HTMLElement>(
  animationConfig: AnimationConfig,
  animatedElement: E,
  animationState: PermittedAnimationState<V>,
  onAnimationEnd: UseAnimationConfig<PermittedAnimationState<V>, E>['onAnimationEnd']
) {
  const { delay, duration, easing } = animationConfig.options || {};

  //Now, if user does not pass a value we override it by hand. We will use theming tools in future.
  const animationOptions = {
    delay: delay || 0,
    duration: duration || 400,
    easing: easing
      ? typeof easing === 'string'
        ? easing
        : `cubic-bezier(${easing[0]}, ${easing[1]}, ${easing[2]}, ${easing[3]})`
      : 'ease'
  };

  // WAAPI animation method. https://developer.mozilla.org/en-US/docs/Web/API/Element/animate
  // TO DO: Check for required browser compatibility in case we need pollyfill.
  // https://github.com/web-animations/web-animations-js/blob/dev/docs/support.md#browser-support
  const startedAnimation = animatedElement.animate(
    [
      convertUseAnimationCssPropertiesToWAAPIKeyframe(animationConfig.from),
      convertUseAnimationCssPropertiesToWAAPIKeyframe(animationConfig.to)
    ],
    { ...animationOptions }
  );

  startedAnimation.onfinish = () => {
    //We commit styles to have an only source of truth.
    setEndStyle(animatedElement, true, animationConfig);
    // On animationEnd is only called when animation actually finished. If an animation is interruped by other one
    // or animated element is not longer rendered onAnimationEnd will not be called. Do we want to have a callback for those cases?
    onAnimationEnd?.({ animationState });
  };

  return startedAnimation;
}

/**
 * Sets element style.
 *
 * @param animatedElement Element that is going to be styled.
 * @param toAndEndStyle Represents which data will be used to set style. If set to true it uses "to" and "endStyle".
 * If set to false it uses just "endStyle".
 * @param animationConfig Style that is going to be set.
 */
function setEndStyle<E extends HTMLElement>(
  animatedElement: E,
  toAndEndStyle: boolean,
  animationConfig: AnimationConfig
) {
  const endStyle = toAndEndStyle
    ? {
        ...convertUseAnimationCssPropertiesToRegularCSS(animationConfig.to),
        ...convertUseAnimationCssPropertiesToRegularCSS(animationConfig.end)
      }
    : convertUseAnimationCssPropertiesToRegularCSS(animationConfig.end);
  for (const key in endStyle) {
    //TO DO: Look for a better way to handle types.
    (<any>animatedElement.style)[key] = endStyle[key as keyof typeof endStyle];
  }
}
